import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import apiClient from '../lib/api';
import { calculateYearTotals } from '../utils/calculateYearTotals';

const Auth0DataContext = createContext();

export const useAuth0Data = () => {
  const context = useContext(Auth0DataContext);
  if (!context) {
    throw new Error('useAuth0Data must be used within an Auth0DataProvider');
  }
  return context;
};

export const Auth0DataProvider = ({ children }) => {
  const { user: auth0User, isLoading: auth0Loading, isAuthenticated, logout: auth0Logout, getAccessTokenSilently } = useAuth0();
  const [allSystemUsers, setAllSystemUsers] = useState([]);
  // Unified year state - controls all year-filtered data (Revenue/GM, Business Lines, Offers, Sales Team)
  const [dataYear, setDataYearState] = useState(new Date().getFullYear());
  const [dataAvailableYears, setDataAvailableYears] = useState([new Date().getFullYear()]);
  const [data, setData] = useState({
    revenue: { current: 0, goal: 0, ytd: 0, pace: 0 },
    grossMargin: { current: 0, goal: 0, ytd: 0, pace: 0 },
    historicalRevenueGM: [],
    salesTeam: [],
    businessLines: [],
    offers: [],
    meetings: [],
    projects: [],
    projectColumns: []
  });

  // Refresh the access token - used by session monitor and API client
  // forceNew: true will bypass cache (used on 401 errors), false uses cached token if valid
  const refreshToken = useCallback(async (forceNew = false) => {
    try {
      console.log(`🔄 Refreshing access token... (forceNew: ${forceNew})`);
      const options = {
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      };

      // Only force cache bypass if explicitly requested (e.g., on 401 error)
      // This avoids requiring refresh tokens for proactive refreshes
      if (forceNew) {
        options.cacheMode = 'off';
      }

      const token = await getAccessTokenSilently(options);
      apiClient.setToken(token);
      console.log('✅ Token refreshed successfully');
      return token;
    } catch (error) {
      console.error('❌ Failed to refresh token:', error);
      throw error;
    }
  }, [getAccessTokenSilently]);

  // Handle auth failure - log the user out
  const handleAuthFailure = useCallback(() => {
    console.log('🚪 Auth failure detected, logging out...');
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  // Set API token and load data when authenticated
  useEffect(() => {
    const initializeApp = async () => {
      if (isAuthenticated && auth0User) {
        try {
          // First, get and set the token with the API audience
          console.log('🔑 Getting access token with audience...');
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              scope: 'openid profile email',
            },
          });
          apiClient.setToken(token);

          // Set up token refresh and auth failure handlers on the API client
          apiClient.setTokenRefresher(refreshToken);
          apiClient.setAuthFailureHandler(handleAuthFailure);

          console.log('✅ API token set successfully');

          // Then, load data and ensure profile
          await Promise.all([
            ensureUserProfile(),
            loadSharedData()
          ]);
        } catch (error) {
          console.error('❌ Error initializing app:', error);
        }
      }
    };
    initializeApp();
  }, [isAuthenticated, auth0User, getAccessTokenSilently, refreshToken, handleAuthFailure]);

  // Keep a ref to loadSharedData so the event listener always has the latest version
  const loadSharedDataRef = React.useRef(null);

  const ensureUserProfile = async () => {
    if (!auth0User) return;
    try {
      console.log('🔄 Ensuring user profile exists for:', auth0User.email);

      await apiClient.ensureUserProfile(
        auth0User.sub,
        auth0User.email,
        auth0User.name || auth0User.email?.split('@')[0] || 'User'
      );

      console.log('✅ Profile ensured successfully');
    } catch (error) {
      console.error('❌ Error ensuring user profile:', error);
    }
  };


  const loadSharedData = async (yearOverride = null) => {
    if (!auth0User) return;
    try {
      const yearToLoad = yearOverride || dataYear;
      console.log('🔄 Loading SHARED data (all users see same data)...');
      console.log(`📅 Loading data for year: ${yearToLoad}`);
      console.log(`🔄 loadSharedData: Current apiClient.teamId: ${apiClient.getTeamId()}`);

      // Load all shared data from backend API (with year filtering where applicable)
      const [
        profilesResult,
        businessLinesResult,
        businessLinesYearsResult,
        salesTeamResult,
        salesTeamYearsResult,
        meetingsResult,
        offersResult,
        offersYearsResult,
        projectsResult,
        projectColumnsResult,
        historicalDataResult,
        revenueGMResult,
        revenueGMYearsResult
      ] = await Promise.allSettled([
        apiClient.getAllProfiles(),
        apiClient.getBusinessLines(yearToLoad),
        apiClient.getBusinessLinesYears(),
        apiClient.getSalesTeam(yearToLoad),
        apiClient.getSalesTeamYears(),
        apiClient.getMeetings(),
        apiClient.getOffers(yearToLoad),
        apiClient.getOffersYears(),
        apiClient.getProjects(),
        apiClient.getProjectColumns(),
        apiClient.getHistoricalRevenueGm(),
        apiClient.getRevenueGm(yearToLoad),
        apiClient.getRevenueGmYears()
      ]);

      // Process results safely
      const profiles = profilesResult.status === 'fulfilled' ? profilesResult.value || [] : [];
      setAllSystemUsers(profiles.map(p => ({ id: p.id, name: p.name, email: p.email, role: p.role })));

      const businessLines = businessLinesResult.status === 'fulfilled' ? businessLinesResult.value || [] : [];
      const businessLinesYears = businessLinesYearsResult.status === 'fulfilled' ? businessLinesYearsResult.value || [] : [];
      const salesTeam = salesTeamResult.status === 'fulfilled' ? salesTeamResult.value || [] : [];
      const salesTeamYears = salesTeamYearsResult.status === 'fulfilled' ? salesTeamYearsResult.value || [] : [];
      const meetings = meetingsResult.status === 'fulfilled' ? meetingsResult.value || [] : [];
      const offers = offersResult.status === 'fulfilled' ? offersResult.value || [] : [];
      const offersYears = offersYearsResult.status === 'fulfilled' ? offersYearsResult.value || [] : [];
      const projects = projectsResult.status === 'fulfilled' ? projectsResult.value || [] : [];
      const projectColumnsData = projectColumnsResult.status === 'fulfilled' ? projectColumnsResult.value || [] : [];
      const historicalData = historicalDataResult.status === 'fulfilled' ? historicalDataResult.value || [] : [];
      const revenueGMData = revenueGMResult.status === 'fulfilled' ? revenueGMResult.value : null;
      const revenueGMYears = revenueGMYearsResult.status === 'fulfilled' ? revenueGMYearsResult.value || [] : [];

      // Update available years - union of all years from all data types, ensure current year is always available
      const currentYear = new Date().getFullYear();
      const allYears = [...salesTeamYears, ...businessLinesYears, ...offersYears, ...revenueGMYears, currentYear];
      const availableYears = [...new Set(allYears)].sort((a, b) => b - a);
      setDataAvailableYears(availableYears);
      console.log(`📅 Available data years: ${availableYears.join(', ')}`);
      console.log(`📅 Loaded ${salesTeam.length} sales team members, ${businessLines.length} business lines, ${offers.length} offers for year ${yearToLoad}`);

      // Transform and filter historical data - remove any records with null/invalid dates
      console.log(`📥 Raw historical data from API (${historicalData.length} records):`, historicalData);

      const transformedHistoricalData = historicalData
        .filter(item => {
          console.log(`🔍 Checking item:`, {
            id: item.id,
            date: item.date,
            dateType: typeof item.date,
            dateTruthy: !!item.date,
            revenue: item.revenue,
            grossMargin: item.grossMargin
          });

          if (!item.date) {
            console.warn('⚠️ Filtering out historical data point with null date:', item.id);
            return false;
          }
          const dateObj = typeof item.date === 'string' ? new Date(item.date) : item.date;
          const isValid = (dateObj instanceof Date) && !isNaN(dateObj.getTime());
          console.log(`🔍 Date validation: dateObj=${dateObj}, isValidDate=${isValid}`);

          if (!isValid) {
            console.warn('⚠️ Filtering out historical data point with invalid date:', item.id, dateObj);
            return false;
          }
          console.log('✅ Item passed filtering');
          return true;
        })
        .map(item => ({
          id: item.id,
          date: item.date,
          revenue: parseFloat(item.revenue) || 0,
          grossMargin: parseFloat(item.grossMargin) || 0,
          description: item.description,
          type: item.type
        }));

      console.log(`📊 Loaded ${historicalData.length} historical records, ${transformedHistoricalData.length} valid after filtering`);
      console.log(`📊 Transformed data:`, transformedHistoricalData);

      // Calculate current values using the corrected logic
      const { totalRevenue, totalGrossMargin } = calculateYearTotals(transformedHistoricalData);

      // Create default revenue/GM record if it doesn't exist (shared)
      if (!revenueGMData) {
        console.log('📝 Creating shared revenue/GM record...');
        try {
          await apiClient.updateRevenue({
            current: totalRevenue,
            goal: 0,
            ytd: 0,
            pace: 0
          });
          await apiClient.updateGrossMargin({
            current: totalGrossMargin,
            goal: 0,
            ytd: 0,
            pace: 0
          });
        } catch (error) {
          console.error('❌ Error creating revenue/GM record:', error);
        }
      }

      // Update state with shared data
      setData({
        revenue: {
          current: totalRevenue,
          goal: revenueGMData ? parseFloat(revenueGMData.revenueGoal) || 0 : 0,
          ytd: revenueGMData ? parseFloat(revenueGMData.revenueYtd) || 0 : 0,
          pace: revenueGMData ? parseFloat(revenueGMData.revenuePace) || 0 : 0
        },
        grossMargin: {
          current: totalGrossMargin,
          goal: revenueGMData ? parseFloat(revenueGMData.grossMarginGoal) || 0 : 0,
          ytd: revenueGMData ? parseFloat(revenueGMData.grossMarginYtd) || 0 : 0,
          pace: revenueGMData ? parseFloat(revenueGMData.grossMarginPace) || 0 : 0
        },
        historicalRevenueGM: transformedHistoricalData,
        businessLines: businessLines.map(item => ({
          id: item.id,
          name: item.name,
          year: item.year || currentYear,
          revenue: parseFloat(item.revenue) || 0,
          goal: parseFloat(item.goal) || 0,
          margin: parseFloat(item.margin) || 0
        })),
        salesTeam: salesTeam.map(member => ({
          id: member.id,
          name: member.name,
          role: member.role,
          year: member.year || currentYear,
          gmCurrent: parseFloat(member.gmCurrent) || 0,
          gmGoal: parseFloat(member.gmGoal) || 0,
          meetings: member.meetings || 0,
          meetingsGoal: member.meetingsGoal || 0,
          msas: member.msas || 0,
          msasGoal: member.msasGoal || 0,
          jobs: member.jobs || 0,
          jobsGoal: member.jobsGoal || 0,
          qtdPlacements: member.qtdPlacements || 0,
          qtdPlacementsGoal: member.qtdPlacementsGoal || 0,
          qtdIntake: member.qtdIntake || 0,
          qtdIntakeGoal: member.qtdIntakeGoal || 0,
          ytdPlacements: member.ytdPlacements || 0,
          ytdPlacementsGoal: member.ytdPlacementsGoal || 0,
          ytdIntake: member.ytdIntake || 0,
          ytdIntakeGoal: member.ytdIntakeGoal || 0
        })),
        meetings: meetings.map(item => ({
          id: item.id,
          title: item.title,
          client: item.client,
          date: item.date,
          status: item.status
        })),
        offers: offers.map(item => ({
          id: item.id,
          title: item.title,
          client: item.client,
          year: item.year || currentYear,
          value: parseFloat(item.value) || 0,
          status: item.status
        })),
        projects: projects.map(project => ({
          id: project.id,
          title: project.title,
          description: project.description,
          owner: project.owner,
          dueDate: project.dueDate,
          status: project.status,
          priority: project.priority || 'medium',
          tasks: Array.isArray(project.tasks) ? project.tasks : typeof project.tasks === 'string' ? JSON.parse(project.tasks || '[]') : []
        })),
        projectColumns: projectColumnsData.map(col => ({
          id: col.id,
          title: col.title,
          color: col.color,
          order: col.order
        }))
      });

      // Initialize default columns if none exist (one-time setup)
      if (projectColumnsData.length === 0) {
        console.log('📝 No project columns found, initializing defaults...');
        const DEFAULT_COLUMNS = [
          { title: 'Planning', color: 'bg-blue-100' },
          { title: 'In Progress', color: 'bg-yellow-100' },
          { title: 'Review', color: 'bg-purple-100' },
          { title: 'Completed', color: 'bg-green-100' }
        ];
        try {
          const result = await apiClient.replaceProjectColumns(DEFAULT_COLUMNS);
          setData(prev => ({
            ...prev,
            projectColumns: result.map(col => ({
              id: col.id,
              title: col.title,
              color: col.color,
              order: col.order
            }))
          }));
          console.log('✅ Default project columns initialized');
        } catch (initError) {
          console.error('❌ Error initializing default columns:', initError);
        }
      }

      console.log('✅ Shared data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading shared data:', error);
    }
  };

  // Update ref to always point to the latest loadSharedData
  loadSharedDataRef.current = loadSharedData;

  // Listen for team switch events and reload data
  useEffect(() => {
    const handleTeamSwitch = (event) => {
      console.log('🔄 Auth0DataContext: Team switched event received for team:', event.detail?.team?.name);
      console.log('🔄 Auth0DataContext: apiClient.teamId at event time:', apiClient.getTeamId());
      // Use ref to always call the latest version of loadSharedData
      if (loadSharedDataRef.current) {
        loadSharedDataRef.current();
      }
    };

    window.addEventListener('team-switched', handleTeamSwitch);
    return () => window.removeEventListener('team-switched', handleTeamSwitch);
  }, []);

  // Auth functions - Auth0 uses different patterns
  const signOut = () => {
    console.log('🔄 Signing out user');
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Data management functions
  const updateData = async (category, newData) => {
    if (!auth0User) {
      console.error('❌ User not authenticated');
      throw new Error('User not authenticated');
    }
    try {
      console.log(`🔄 Updating shared ${category} with data:`, newData);
      if (category === 'historicalRevenueGM') {
        console.log(`📊 Historical data details - ${newData.length} records:`);
        newData.forEach((item, index) => {
          console.log(`  [${index}]:`, {
            id: item.id,
            date: item.date,
            dateType: typeof item.date,
            revenue: item.revenue,
            grossMargin: item.grossMargin,
            type: item.type
          });
        });
      }

      if (category === 'revenue') {
        await apiClient.updateRevenue(newData);
      } else if (category === 'grossMargin') {
        await apiClient.updateGrossMargin(newData);
      } else if (category === 'historicalRevenueGM') {
        // Replace all historical data
        console.log(`📤 About to call replaceHistoricalData with ${newData.length} records`);
        try {
          await apiClient.replaceHistoricalData(newData);
          console.log(`✅ replaceHistoricalData call completed successfully`);
        } catch (apiError) {
          console.error(`❌ replaceHistoricalData API call failed:`, apiError);
          throw apiError; // Re-throw to be caught by outer try-catch
        }

        // Update current totals
        const { totalRevenue, totalGrossMargin } = calculateYearTotals(newData);
        await apiClient.updateRevenue({
          current: totalRevenue,
          goal: data.revenue.goal,
          ytd: data.revenue.ytd,
          pace: data.revenue.pace
        });
        await apiClient.updateGrossMargin({
          current: totalGrossMargin,
          goal: data.grossMargin.goal,
          ytd: data.grossMargin.ytd,
          pace: data.grossMargin.pace
        });
      }
      // Update local state
      setData(prev => ({ ...prev, [category]: newData }));
      console.log(`✅ Successfully updated shared ${category}`);
    } catch (error) {
      console.error(`❌ Error updating ${category}:`, error);
      throw error;
    }
  };

  const addItem = async (category, item) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log('🔄 Adding shared item to category:', category);
      let result;

      switch (category) {
        case 'businessLines':
          result = await apiClient.createBusinessLine(item);
          break;
        case 'salesTeam':
          result = await apiClient.createSalesTeamMember(item);
          break;
        case 'meetings':
          result = await apiClient.createMeeting(item);
          break;
        case 'offers':
          result = await apiClient.createOffer(item);
          break;
        case 'projects':
          result = await apiClient.createProject(item);
          break;
        case 'projectColumns':
          result = await apiClient.createProjectColumn(item);
          break;
        default:
          throw new Error(`Unknown category: ${category}`);
      }

      // Update local state
      const newItem = { ...item, id: result.id };
      setData(prev => ({
        ...prev,
        [category]: Array.isArray(prev[category]) ? [...prev[category], newItem] : prev[category]
      }));
      console.log('✅ Shared item added successfully');
    } catch (error) {
      console.error(`❌ Error adding ${category} item:`, error);
      throw error;
    }
  };

  const updateItem = async (category, id, updates) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log('🔄 Updating shared item:', { category, id, updates });

      switch (category) {
        case 'businessLines':
          await apiClient.updateBusinessLine(id, updates);
          break;
        case 'salesTeam':
          await apiClient.updateSalesTeamMember(id, updates);
          break;
        case 'meetings':
          await apiClient.updateMeeting(id, updates);
          break;
        case 'offers':
          await apiClient.updateOffer(id, updates);
          break;
        case 'projects':
          await apiClient.updateProject(id, updates);
          break;
        case 'projectColumns':
          await apiClient.updateProjectColumn(id, updates);
          break;
        default:
          throw new Error(`Unknown category: ${category}`);
      }

      // Update local state
      setData(prev => ({
        ...prev,
        [category]: Array.isArray(prev[category]) ? prev[category].map(item =>
          item.id === id ? { ...item, ...updates } : item
        ) : prev[category]
      }));
      console.log('✅ Shared item updated successfully');
    } catch (error) {
      console.error(`❌ Error updating ${category} item:`, error);
      throw error;
    }
  };

  const deleteItem = async (category, id) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log('🔄 Deleting shared item:', { category, id });

      switch (category) {
        case 'businessLines':
          await apiClient.deleteBusinessLine(id);
          break;
        case 'salesTeam':
          await apiClient.deleteSalesTeamMember(id);
          break;
        case 'meetings':
          await apiClient.deleteMeeting(id);
          break;
        case 'offers':
          await apiClient.deleteOffer(id);
          break;
        case 'projects':
          await apiClient.deleteProject(id);
          break;
        case 'projectColumns':
          await apiClient.deleteProjectColumn(id);
          break;
        default:
          throw new Error(`Unknown category: ${category}`);
      }

      // Update local state
      setData(prev => ({
        ...prev,
        [category]: Array.isArray(prev[category]) ? prev[category].filter(item => item.id !== id) : prev[category]
      }));
      console.log('✅ Shared item deleted successfully');
    } catch (error) {
      console.error(`❌ Error deleting ${category} item:`, error);
      throw error;
    }
  };

  // Get all users for dropdowns
  const getAllUsers = () => {
    return allSystemUsers;
  };

  // Replace all project columns (for bulk operations)
  const replaceProjectColumns = async (columns) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log('🔄 Replacing all project columns:', columns);
      const result = await apiClient.replaceProjectColumns(columns);
      setData(prev => ({
        ...prev,
        projectColumns: result.map(col => ({
          id: col.id,
          title: col.title,
          color: col.color,
          order: col.order
        }))
      }));
      console.log('✅ Project columns replaced successfully');
      return result;
    } catch (error) {
      console.error('❌ Error replacing project columns:', error);
      throw error;
    }
  };

  // Set the data year and reload all year-filtered data
  const setDataYear = async (year) => {
    console.log(`📅 Changing data year to: ${year}`);
    setDataYearState(year);

    // Reload all year-filtered data for the new year
    try {
      const currentYear = new Date().getFullYear();
      const [salesTeamResult, businessLinesResult, offersResult, revenueGMResult] = await Promise.allSettled([
        apiClient.getSalesTeam(year),
        apiClient.getBusinessLines(year),
        apiClient.getOffers(year),
        apiClient.getRevenueGm(year)
      ]);

      const salesTeam = salesTeamResult.status === 'fulfilled' ? salesTeamResult.value || [] : [];
      const businessLines = businessLinesResult.status === 'fulfilled' ? businessLinesResult.value || [] : [];
      const offers = offersResult.status === 'fulfilled' ? offersResult.value || [] : [];
      const revenueGMData = revenueGMResult.status === 'fulfilled' ? revenueGMResult.value : null;

      setData(prev => ({
        ...prev,
        revenue: {
          current: prev.revenue.current, // Keep calculated current from historical data
          goal: revenueGMData ? parseFloat(revenueGMData.revenueGoal) || 0 : 0,
          ytd: revenueGMData ? parseFloat(revenueGMData.revenueYtd) || 0 : 0,
          pace: revenueGMData ? parseFloat(revenueGMData.revenuePace) || 0 : 0
        },
        grossMargin: {
          current: prev.grossMargin.current, // Keep calculated current from historical data
          goal: revenueGMData ? parseFloat(revenueGMData.grossMarginGoal) || 0 : 0,
          ytd: revenueGMData ? parseFloat(revenueGMData.grossMarginYtd) || 0 : 0,
          pace: revenueGMData ? parseFloat(revenueGMData.grossMarginPace) || 0 : 0
        },
        businessLines: businessLines.map(item => ({
          id: item.id,
          name: item.name,
          year: item.year || currentYear,
          revenue: parseFloat(item.revenue) || 0,
          goal: parseFloat(item.goal) || 0,
          margin: parseFloat(item.margin) || 0
        })),
        salesTeam: salesTeam.map(member => ({
          id: member.id,
          name: member.name,
          role: member.role,
          year: member.year || currentYear,
          gmCurrent: parseFloat(member.gmCurrent) || 0,
          gmGoal: parseFloat(member.gmGoal) || 0,
          meetings: member.meetings || 0,
          meetingsGoal: member.meetingsGoal || 0,
          msas: member.msas || 0,
          msasGoal: member.msasGoal || 0,
          jobs: member.jobs || 0,
          jobsGoal: member.jobsGoal || 0,
          qtdPlacements: member.qtdPlacements || 0,
          qtdPlacementsGoal: member.qtdPlacementsGoal || 0,
          qtdIntake: member.qtdIntake || 0,
          qtdIntakeGoal: member.qtdIntakeGoal || 0,
          ytdPlacements: member.ytdPlacements || 0,
          ytdPlacementsGoal: member.ytdPlacementsGoal || 0,
          ytdIntake: member.ytdIntake || 0,
          ytdIntakeGoal: member.ytdIntakeGoal || 0
        })),
        offers: offers.map(item => ({
          id: item.id,
          title: item.title,
          client: item.client,
          year: item.year || currentYear,
          value: parseFloat(item.value) || 0,
          status: item.status
        }))
      }));
      console.log(`✅ All data reloaded for year ${year}`);
    } catch (error) {
      console.error(`❌ Error loading data for year ${year}:`, error);
      throw error;
    }
  };

  // Initialize a new year by copying from previous year (Sales Team)
  const initializeSalesTeamYear = async (year) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log(`📅 Initializing sales team for year ${year}...`);
      const result = await apiClient.initializeSalesTeamYear(year);
      console.log(`✅ Initialized ${result.count} team members for ${year}`);

      // Add the new year to available years
      setDataAvailableYears(prev => {
        const updated = [...new Set([...prev, year])].sort((a, b) => b - a);
        return updated;
      });

      // Switch to the new year and load data
      await setDataYear(year);

      return result;
    } catch (error) {
      console.error(`❌ Error initializing sales team year ${year}:`, error);
      throw error;
    }
  };

  // Initialize a new year by copying from previous year (Business Lines)
  const initializeBusinessLinesYear = async (year) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log(`📅 Initializing business lines for year ${year}...`);
      const result = await apiClient.initializeBusinessLinesYear(year);
      console.log(`✅ Initialized ${result.count} business lines for ${year}`);

      // Add the new year to available years
      setDataAvailableYears(prev => {
        const updated = [...new Set([...prev, year])].sort((a, b) => b - a);
        return updated;
      });

      // Switch to the new year and load data
      await setDataYear(year);

      return result;
    } catch (error) {
      console.error(`❌ Error initializing business lines year ${year}:`, error);
      throw error;
    }
  };

  // Initialize a new year by copying from previous year (Offers)
  const initializeOffersYear = async (year) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log(`📅 Initializing offers for year ${year}...`);
      const result = await apiClient.initializeOffersYear(year);
      console.log(`✅ Initialized ${result.count} offers for ${year}`);

      // Add the new year to available years
      setDataAvailableYears(prev => {
        const updated = [...new Set([...prev, year])].sort((a, b) => b - a);
        return updated;
      });

      // Switch to the new year and load data
      await setDataYear(year);

      return result;
    } catch (error) {
      console.error(`❌ Error initializing offers year ${year}:`, error);
      throw error;
    }
  };

  // Initialize a new year for Revenue/GM
  const initializeRevenueGmYear = async (year) => {
    if (!auth0User) throw new Error('User not authenticated');
    try {
      console.log(`📅 Initializing revenue/GM for year ${year}...`);
      const result = await apiClient.initializeRevenueGmYear(year);
      console.log(`✅ Initialized revenue/GM for ${year}`);

      // Add the new year to available years
      setDataAvailableYears(prev => {
        const updated = [...new Set([...prev, year])].sort((a, b) => b - a);
        return updated;
      });

      // Switch to the new year and load data
      await setDataYear(year);

      return result;
    } catch (error) {
      console.error(`❌ Error initializing revenue/GM year ${year}:`, error);
      throw error;
    }
  };

  // Refresh available years from all data sources
  const refreshDataYears = async () => {
    try {
      const [salesTeamYears, businessLinesYears, offersYears, revenueGMYears] = await Promise.allSettled([
        apiClient.getSalesTeamYears(),
        apiClient.getBusinessLinesYears(),
        apiClient.getOffersYears(),
        apiClient.getRevenueGmYears()
      ]);

      const currentYear = new Date().getFullYear();
      const allYears = [
        ...(salesTeamYears.status === 'fulfilled' ? salesTeamYears.value || [] : []),
        ...(businessLinesYears.status === 'fulfilled' ? businessLinesYears.value || [] : []),
        ...(offersYears.status === 'fulfilled' ? offersYears.value || [] : []),
        ...(revenueGMYears.status === 'fulfilled' ? revenueGMYears.value || [] : []),
        currentYear
      ];
      const availableYears = [...new Set(allYears)].sort((a, b) => b - a);
      setDataAvailableYears(availableYears);
      return availableYears;
    } catch (error) {
      console.error('❌ Error refreshing data years:', error);
      throw error;
    }
  };

  const value = {
    user: auth0User,
    loading: auth0Loading,
    isAuthenticated,
    data,
    signOut,
    refreshToken,
    updateData,
    addItem,
    updateItem,
    deleteItem,
    getAllUsers,
    loadUserData: loadSharedData,
    replaceProjectColumns,
    // Unified year management (controls Revenue/GM, Business Lines, Offers, Sales Team)
    dataYear,
    dataAvailableYears,
    setDataYear,
    refreshDataYears,
    // Initialize year functions
    initializeSalesTeamYear,
    initializeBusinessLinesYear,
    initializeOffersYear,
    initializeRevenueGmYear,
    // Backward compatibility aliases (used by existing widgets)
    salesTeamYear: dataYear,
    salesTeamAvailableYears: dataAvailableYears,
    setSalesTeamYear: setDataYear
  };

  return (
    <Auth0DataContext.Provider value={value}>
      {children}
    </Auth0DataContext.Provider>
  );
};
